import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../theme';
import { QuestionType, SurveyQuestion } from '../Modals/AddSurveyModal/addSurveyInterface';
import {
    CollapseSpacerStyled,
    CollapseToggleStyled,
    QuestionItemStyled,
    QuestionListContainerStyled,
    QuestionListHeaderStyled,
    QuestionToolbarStyled,
    QuestionTypeBadgeStyled,
    SelectActionButtonStyled,
    SelectionActionsRowStyled,
    SelectionSeparatorStyled,
    TypeFilterChipStyled,
    TypeFilterRowStyled,
} from './styled';

const NON_SELECTABLE_TYPES: QuestionType[] = ['Chapter', 'Loop'];

const TYPE_CONFIG: Record<QuestionType, { color: string; label: string }> = {
    Single: { color: '#3B82F6', label: 'S' },
    Multiple: { color: '#8B5CF6', label: 'M' },
    Open: { color: '#22C55E', label: 'O' },
    Date: { color: '#F97316', label: 'D' },
    DateTime: { color: '#F97316', label: 'D' },
    Chapter: { color: '#6366F1', label: 'C' },
    Loop: { color: '#14777B', label: 'L' },
    Numeric: { color: '#f91616ff', label: 'N' },
};

interface QuestionListPanelProps {
    questions: SurveyQuestion[];
    selectedIds: number[];
    onSelectionChange: (ids: number[]) => void;
    title?: string;
}

const QuestionListPanel: FC<QuestionListPanelProps> = ({
    questions,
    selectedIds,
    onSelectionChange,
    title,
}) => {
    const { t } = useTranslation('settings');
    const lastClickedIndexRef = useRef<number>(-1);

    const [activeTypeFilters, setActiveTypeFilters] = useState<QuestionType[]>([]);
    const [collapsedIds, setCollapsedIds] = useState<number[]>([]);
    const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');

    const questionMap = useMemo(
        () => new Map(questions.map((q) => [q.id, q])),
        [questions],
    );

    const depths = useMemo(() => {
        const map = new Map<number, number>();

        const getDepth = (id: number, visited: Set<number> = new Set()): number => {
            if (map.has(id)) { return map.get(id)!; }
            if (visited.has(id)) { return 0; }
            const q = questionMap.get(id);
            if (!q || q.details.parentid === 0 || q.details.parentid === id) {
                map.set(id, 0);

                return 0;
            }
            visited.add(id);
            const parentDepth = getDepth(q.details.parentid, visited);
            const depth = parentDepth + 1;
            map.set(id, depth);

            return depth;
        };

        questions.forEach((q) => getDepth(q.id));

        return map;
    }, [questions, questionMap]);

    const childrenMap = useMemo(() => {
        const map = new Map<number, number[]>();
        questions.forEach((q) => {
            if (q.details.parentid !== 0) {
                const children = map.get(q.details.parentid) ?? [];
                children.push(q.id);
                map.set(q.details.parentid, children);
            }
        });

        return map;
    }, [questions]);

    const availableTypes = useMemo(
        () => Array.from(new Set(questions.map((q) => q.details.type))) as QuestionType[],
        [questions],
    );

    const isDescendantOfCollapsed = useCallback(
        (question: SurveyQuestion): boolean => {
            let { parentid } = question.details;
            while (parentid !== 0) {
                if (collapsedIds.includes(parentid)) { return true; }
                const parent = questionMap.get(parentid);
                if (!parent) { break; }
                parentid = parent.details.parentid;
            }

            return false;
        },
        [collapsedIds, questionMap],
    );

    const typeFilteredQuestions = useMemo(
        () => (activeTypeFilters.length === 0
            ? questions
            : questions.filter((q) => activeTypeFilters.includes(q.details.type))),
        [questions, activeTypeFilters],
    );

    const visibilityFilteredQuestions = useMemo(() => {
        if (visibilityFilter === 'visible') { return typeFilteredQuestions.filter((q) => q.details.visibility); }
        if (visibilityFilter === 'hidden') { return typeFilteredQuestions.filter((q) => !q.details.visibility); }

        return typeFilteredQuestions;
    }, [typeFilteredQuestions, visibilityFilter]);

    const displayedQuestions = useMemo(
        () => visibilityFilteredQuestions.filter((q) => !isDescendantOfCollapsed(q)),
        [visibilityFilteredQuestions, isDescendantOfCollapsed],
    );

    const handleToggleVisibilityFilter = (value: 'visible' | 'hidden') => {
        setVisibilityFilter((prev) => (prev === value ? 'all' : value));
        lastClickedIndexRef.current = -1;
    };

    const handleToggleTypeFilter = (type: QuestionType) => {
        setActiveTypeFilters((prev) => (prev.includes(type) ? prev.filter((f) => f !== type) : [...prev, type]));
        lastClickedIndexRef.current = -1;
    };

    const handleToggleCollapse = (questionId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setCollapsedIds((prev) => (prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId]));
        lastClickedIndexRef.current = -1;
    };

    const handleSelectAll = () => {
        const visibleIds = displayedQuestions
            .filter((q) => !NON_SELECTABLE_TYPES.includes(q.details.type))
            .map((q) => q.id);
        onSelectionChange(Array.from(new Set([...selectedIds, ...visibleIds])));
    };

    const handleDeselectAll = () => {
        onSelectionChange([]);
    };

    const handleClick = useCallback(
        (question: SurveyQuestion, index: number, event: React.MouseEvent) => {
            if (NON_SELECTABLE_TYPES.includes(question.details.type)) { return; }
            if (event.shiftKey && lastClickedIndexRef.current >= 0) {
                const start = Math.min(lastClickedIndexRef.current, index);
                const end = Math.max(lastClickedIndexRef.current, index);
                const rangeIds = displayedQuestions
                    .slice(start, end + 1)
                    .filter((q) => !NON_SELECTABLE_TYPES.includes(q.details.type))
                    .map((q) => q.id);
                onSelectionChange(Array.from(new Set([...selectedIds, ...rangeIds])));
            } else {
                const isSelected = selectedIds.includes(question.id);
                onSelectionChange(
                    isSelected
                        ? selectedIds.filter((id) => id !== question.id)
                        : [...selectedIds, question.id],
                );
                lastClickedIndexRef.current = index;
            }
        },
        [displayedQuestions, selectedIds, onSelectionChange],
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {title && (
                <QuestionListHeaderStyled>
                    <TypographyStyledGlobal
                        color={colors.grey200}
                        size={fontSize.sm}
                        weight={fontWeight.semiBold}
                    >
                        {title}
                    </TypographyStyledGlobal>
                    <TypographyStyledGlobal color={colors.grey400} size={fontSize.s} weight={fontWeight.semiRegular}>
                        {t('tabs.createSurvey.step3Content.selectedCount', { count: selectedIds.length })}
                    </TypographyStyledGlobal>
                </QuestionListHeaderStyled>
            )}
            <QuestionToolbarStyled>
                <TypeFilterRowStyled>
                    <TypeFilterChipStyled
                        active={visibilityFilter === 'visible'}
                        chipColor={colors.alertSuccess}
                        onClick={() => handleToggleVisibilityFilter('visible')}
                        type="button"
                    >
                        {t('tabs.createSurvey.step3Content.filterVisible')}
                    </TypeFilterChipStyled>
                    <TypeFilterChipStyled
                        active={visibilityFilter === 'hidden'}
                        chipColor={colors.grey300}
                        onClick={() => handleToggleVisibilityFilter('hidden')}
                        type="button"
                    >
                        {t('tabs.createSurvey.step3Content.filterHidden')}
                    </TypeFilterChipStyled>
                </TypeFilterRowStyled>
                {availableTypes.length > 1 && (
                    <TypeFilterRowStyled>
                        {availableTypes.map((type) => {
                            const config = TYPE_CONFIG[type] ?? { color: colors.grey400, label: type };

                            return (
                                <TypeFilterChipStyled
                                    key={type}
                                    active={activeTypeFilters.includes(type)}
                                    chipColor={config.color}
                                    onClick={() => handleToggleTypeFilter(type)}
                                    type="button"
                                >
                                    {t(`tabs.createSurvey.step3Content.questionTypes.${type}`)}
                                </TypeFilterChipStyled>
                            );
                        })}
                    </TypeFilterRowStyled>
                )}
                <SelectionActionsRowStyled>
                    <SelectActionButtonStyled
                        disabled={displayedQuestions.length === 0}
                        onClick={handleSelectAll}
                        type="button"
                    >
                        {t('tabs.createSurvey.step3Content.selectAll')}
                    </SelectActionButtonStyled>
                    <SelectionSeparatorStyled>|</SelectionSeparatorStyled>
                    <SelectActionButtonStyled
                        disabled={selectedIds.length === 0}
                        onClick={handleDeselectAll}
                        type="button"
                    >
                        {t('tabs.createSurvey.step3Content.deselectAll')}
                    </SelectActionButtonStyled>
                </SelectionActionsRowStyled>
            </QuestionToolbarStyled>
            <QuestionListContainerStyled>
                {displayedQuestions.length === 0 ? (
                    <TypographyStyledGlobal color={colors.grey400} size={fontSize.sm}>
                        {t('tabs.createSurvey.step3Content.noQuestions')}
                    </TypographyStyledGlobal>
                ) : (
                    displayedQuestions.map((question, index) => {
                        const depth = depths.get(question.id) ?? 0;
                        const isSelectable = !NON_SELECTABLE_TYPES.includes(question.details.type);
                        const isSelected = isSelectable && selectedIds.includes(question.id);
                        const typeConfig = TYPE_CONFIG[question.details.type] ?? { color: colors.grey400, label: '?' };
                        const isVisible = question.details.visibility;
                        const isParent = childrenMap.has(question.id);
                        const isCollapsed = collapsedIds.includes(question.id);

                        return (
                            <QuestionItemStyled
                                key={question.id}
                                depth={depth}
                                isSelectable={isSelectable}
                                isSelected={isSelected}
                                isVisible={isVisible}
                                onClick={(e: React.MouseEvent) => handleClick(question, index, e)}
                            >
                                {isParent ? (
                                    <CollapseToggleStyled
                                        onClick={(e: React.MouseEvent) => handleToggleCollapse(question.id, e)}
                                        type="button"
                                    >
                                        {isCollapsed
                                            ? <ChevronRightIcon sx={{ fontSize: 14 }} />
                                            : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                                    </CollapseToggleStyled>
                                ) : (
                                    <CollapseSpacerStyled />
                                )}
                                <QuestionTypeBadgeStyled typeColor={typeConfig.color}>
                                    {typeConfig.label}
                                </QuestionTypeBadgeStyled>
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                    <TypographyStyledGlobal
                                        color={isVisible ? colors.grey100 : colors.grey400}
                                        size={fontSize.s}
                                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        weight={fontWeight.regular}
                                    >
                                        {question.details.analysecaption || question.details.maincaption}
                                    </TypographyStyledGlobal>
                                    <TypographyStyledGlobal
                                        color={colors.grey400}
                                        size="11px"
                                        weight={fontWeight.semiRegular}
                                    >
                                        {question.shortcut}
                                    </TypographyStyledGlobal>
                                </div>
                                {!isVisible && (
                                    <VisibilityOffIcon sx={{ fontSize: 14, color: colors.grey400, flexShrink: 0 }} />
                                )}
                            </QuestionItemStyled>
                        );
                    })
                )}
            </QuestionListContainerStyled>
        </div>
    );
};

export default QuestionListPanel;
